import type { LocalRow, SeedTables } from '../types/localDb.types'
import { USER_IDS } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'
import { rmFindingReasonId } from './wms-setup.seed'
import { type DsrStatusCode, type OpnStatusCode, WF_DSR, WF_OPN } from './workflow-opn.seed'

/**
 * Stock Opname C13 (OPN-01 … OPN-06) — ERD v010: `opname_schedule`, `opname_session`, `opname_task`, `opname_scan`,
 * `discrepancy`. Angka mengikuti prototype v7.44 `opn.*` dengan penyesuaian ke unit seed MVDP (laporan 20):
 * - Sesi RM 260818-001 menunggu pengesahan Galih (3 rak: R03 · R01 · R02 kosong), dua selisih Open.
 * - Sesi RM 260818-002 terbuka: tugas R01 untuk Karno (alur operator bisa diperagakan dari seed segar).
 * - Sesi FG 260818-001 Completed (5 area, 1 write-off disetujui Kadiv FG).
 * - Dua selisih "temuan dini" saat picking (RM & FG) menunggu keputusan Kadiv — sumber `PICKING`.
 * Kolom tambahan = [ERD-GAP] (laporan 20): sesi `planned_date, closed_by_id, submitted_at, validated_at, validated_by_id,
 * target_hours, summary, log`; tugas `scope_kind, scope_label, warehouse_id, started_at, locked_at, completed_at,
 * counters, reject_reason, validated_by_id, validated_at, empty_confirmed`; pindai `session_id, unit_id, label_id,
 * pack_stock_id, ng_reason_ids, unit_name, scanned_at, from_location, decision, to_location`; selisih `session_id,
 * warehouse_id, task_id, material_id, sku_id, location_code, scanned_code, unit_name, qty_system, qty_physical, trace,
 * approval_due_at, recheck_operator_id, recheck_note, resolved_at, resolved_by_id, approved_at, reject_reason, log`.
 */

const utc = (at: string) => new Date(`${at.replace(' ', 'T')}:00+07:00`).toISOString()
type UserKey = keyof typeof USER_IDS
const audit = (user: UserKey | null, at: string) => ({
  status: 'published',
  sort: null,
  user_created: user ? USER_IDS[user] : null,
  date_created: utc(at),
  user_updated: null,
  date_updated: null,
  user_deleted: null,
  date_deleted: null
})
const WH_RM = seedId(SEED_GROUP.warehouse, 1)
const WH_FG = seedId(SEED_GROUP.warehouse, 2)

/** Akhir hari ke-`days` sesudah `at` (WIB) — window H+n. */
export const dueEndOfDay = (at: string, days: number) => {
  const wib = new Date(new Date(at).getTime() + 7 * 3600_000)
  const end = Date.UTC(wib.getUTCFullYear(), wib.getUTCMonth(), wib.getUTCDate() + days, 23, 59, 59)
  return new Date(end - 7 * 3600_000).toISOString()
}

type Log = { at: string; ev: string; by: string | null }
const log = (at: string, ev: string, by: UserKey | null): Log => ({ at, ev, by: by ? USER_IDS[by] : null })

export function applyOpnameData(seed: SeedTables): SeedTables {
  const bins = new Map((seed.mt_rack_bin ?? []).map(bin => [String(bin.code), bin]))
  const units = new Map((seed.rm_unit ?? []).map(unit => [String(unit.qr_code), unit]))
  const pallets = new Map((seed.pallet ?? []).map(pallet => [String(pallet.code), pallet]))
  const containers = new Map((seed.container ?? []).map(container => [String(container.code), container]))
  const binId = (code: string) => (bins.get(code)?.id as string | undefined) ?? null
  const unitOf = (qr: string) => units.get(qr) as LocalRow
  const perBig = (unit: LocalRow) => Math.max(1, Number(unit.small_per_big_snapshot ?? 1))

  /* ---------------- unit / label tambahan untuk selisih contoh ---------------- */
  const bold = unitOf('ETK-BOLD20:20260801:01:0002')
  const reg = unitOf('TSG-REG:20260816:01:0001')
  const extraUnits: LocalRow[] = [
    {
      ...bold,
      ...audit('yulia', '2026-08-01 09:10'),
      id: seedId(SEED_GROUP.rmUnit, 901),
      qr_code: 'ETK-BOLD20:20260801:01:0006',
      qty_small: 10 * perBig(bold),
      unit_status: 'MISSING',
      rack_bin_id: binId('R01-B01-L01-P01'),
      stored_at: utc('2026-08-01 09:10')
    },
    {
      ...reg,
      ...audit('karno', '2026-06-30 10:00'),
      id: seedId(SEED_GROUP.rmUnit, 902),
      qr_code: 'TSG-REG:20260630:01:0002',
      qty_small: 0,
      unit_status: 'ADJUSTED_NOT_FOUND',
      rack_bin_id: null,
      stored_at: utc('2026-07-02 10:00')
    }
  ]
  const fgLabels = seed.fg_label ?? []
  const slopTemplate = fgLabels.find(
    label => label.level === 'Slop' && idOf(label.container_id) === containerId('CT-A')
  )
  const kartonTemplate = fgLabels.find(label => idOf(label.pallet_id) === palletId('A1-P2'))
  function idOf(value: unknown) {
    return String(value ?? '')
  }
  function containerId(code: string) {
    return String(containers.get(code)?.id ?? '')
  }
  function palletId(code: string) {
    return String(pallets.get(code)?.id ?? '')
  }
  const extraLabels: LocalRow[] = [
    ...(slopTemplate
      ? [
          {
            ...slopTemplate,
            ...audit('ulil', '2026-08-15 07:04'),
            id: seedId(SEED_GROUP.fgLabel, 9001),
            label_no: 'JLC:S:F:TB20:0109:20260815:070400:B001:5004',
            label_status: 'MISSING'
          }
        ]
      : []),
    ...(kartonTemplate
      ? [
          {
            ...kartonTemplate,
            ...audit('ulil', '2026-08-10 09:37'),
            id: seedId(SEED_GROUP.fgLabel, 9002),
            label_no: 'JLC:C:F:TO12:0103:20260810:093700:B001:1031',
            pallet_id: null,
            label_status: 'ADJUSTED_NOT_FOUND'
          }
        ]
      : [])
  ]

  /* ---------------- jadwal ---------------- */
  const opname_schedule: LocalRow[] = [
    {
      ...audit('yulia', '2026-06-01 08:00'),
      id: seedId(SEED_GROUP.opnameSchedule, 1),
      warehouse_id: WH_RM,
      frequency: 'MONTHLY',
      anchor_day: 15,
      anchor_dow: null,
      mode: 'FULL',
      start_time: '08:00',
      target_hours: 6,
      effective_from: '2026-06-15',
      cyclic_rotation: ['R01', 'R02', 'R03'],
      adhoc_date: null,
      adhoc_reason: null
    },
    {
      ...audit('ulil', '2026-07-01 08:00'),
      id: seedId(SEED_GROUP.opnameSchedule, 2),
      warehouse_id: WH_FG,
      frequency: 'WEEKLY',
      anchor_day: null,
      anchor_dow: 'TUESDAY',
      mode: 'FULL',
      start_time: '08:00',
      target_hours: 4,
      effective_from: '2026-07-07',
      cyclic_rotation: null,
      adhoc_date: null,
      adhoc_reason: null
    }
  ]

  /* ---------------- sesi + submission workflow ---------------- */
  const wxl_submission: LocalRow[] = []
  const submission = (
    no: number,
    entity: string,
    entityId: string,
    statusId: string,
    at: string,
    workflowId: string
  ) => {
    const id = seedId(SEED_GROUP.wxlSubmission, no)
    wxl_submission.push({
      ...audit(null, at),
      id,
      workflow_id: workflowId,
      entity,
      entity_id: entityId,
      source: 'ACTION',
      current_status_id: statusId,
      last_action_id: null,
      workflow_snapshot: null
    })
    return id
  }
  type SessionSpec = {
    n: number
    no: string
    wh: 'RM' | 'FG'
    mode: 'FULL' | 'CYCLIC'
    scope: string
    status: OpnStatusCode
    start: string
    planned: string | null
    closed?: string
    closedBy?: UserKey
    submitted?: string
    validated?: string
    validatedBy?: UserKey
    target: number
    summary?: Record<string, unknown> | null
    log: Log[]
  }
  const sessionSpecs: SessionSpec[] = [
    {
      n: 1,
      no: 'OPN-RM-260615-001',
      wh: 'RM',
      mode: 'CYCLIC',
      scope: 'R01',
      status: 'OPN_COMPLETED',
      start: '2026-06-15 08:00',
      planned: '2026-06-15',
      closed: '2026-06-15 13:20',
      closedBy: 'yulia',
      target: 6,
      summary: {
        locations: 4,
        units: 6,
        matched: 6,
        found: 0,
        adjusted: 0,
        durations: [{ operator: USER_IDS.karno, minutes: 150, scope: 'Rack R01' }]
      },
      log: [log('2026-06-15 13:20', 'Sesi ditutup — 6 matched, 0 selisih', 'yulia')]
    },
    {
      n: 2,
      no: 'OPN-RM-260715-001',
      wh: 'RM',
      mode: 'CYCLIC',
      scope: 'R02',
      status: 'OPN_COMPLETED',
      start: '2026-07-15 08:00',
      planned: '2026-07-15',
      closed: '2026-07-21 16:00',
      closedBy: 'yulia',
      target: 6,
      summary: {
        locations: 5,
        units: 8,
        matched: 7,
        found: 0,
        adjusted: 1,
        durations: [{ operator: USER_IDS.karno, minutes: 180, scope: 'Rack R02' }]
      },
      log: [log('2026-07-21 16:00', 'Sesi ditutup — 7 matched, 1 write-off', 'yulia')]
    },
    {
      n: 3,
      no: 'OPN-RM-260818-001',
      wh: 'RM',
      mode: 'FULL',
      scope: 'R01,R02,R03',
      status: 'OPN_VALIDATION',
      start: '2026-08-18 08:00',
      planned: null,
      submitted: '2026-08-18 10:15',
      target: 6,
      log: [
        log('2026-08-18 08:00', 'Sesi dibuka — 3 tugas hitung dibagikan (Karno, Tono Wijaya)', 'yulia'),
        log('2026-08-18 10:15', 'Seluruh tugas dikunci — sesi menunggu pengesahan Supervisor', 'karno')
      ]
    },
    {
      n: 4,
      no: 'OPN-RM-260818-002',
      wh: 'RM',
      mode: 'CYCLIC',
      scope: 'R01',
      status: 'OPN_COUNTING',
      start: '2026-08-18 13:00',
      planned: null,
      target: 6,
      log: [log('2026-08-18 13:00', 'Sesi dibuka — 1 tugas hitung untuk Karno', 'yulia')]
    },
    {
      n: 5,
      no: 'OPN-FG-260804-001',
      wh: 'FG',
      mode: 'FULL',
      scope: 'A1-P1,B1-P1,CT-C',
      status: 'OPN_COMPLETED',
      start: '2026-08-04 08:00',
      planned: '2026-08-04',
      closed: '2026-08-04 11:10',
      closedBy: 'ulil',
      target: 4,
      summary: {
        locations: 3,
        units: 4,
        matched: 4,
        found: 0,
        adjusted: 0,
        durations: [{ operator: USER_IDS.budi, minutes: 70, scope: '3 area' }]
      },
      log: [log('2026-08-04 11:10', 'Sesi ditutup — 4 matched', 'ulil')]
    },
    {
      n: 6,
      no: 'OPN-FG-260811-001',
      wh: 'FG',
      mode: 'FULL',
      scope: 'A1-P1,A1-P2,CT-A',
      status: 'OPN_COMPLETED',
      start: '2026-08-11 08:00',
      planned: '2026-08-11',
      closed: '2026-08-11 11:40',
      closedBy: 'ulil',
      target: 4,
      summary: {
        locations: 3,
        units: 5,
        matched: 4,
        found: 1,
        adjusted: 0,
        durations: [{ operator: USER_IDS.budi, minutes: 95, scope: '3 area' }]
      },
      log: [log('2026-08-11 11:40', 'Sesi ditutup — 4 matched, 1 ditemukan', 'ulil')]
    },
    {
      n: 7,
      no: 'OPN-FG-260818-001',
      wh: 'FG',
      mode: 'FULL',
      scope: 'A1-P1,A1-P2,B1-P1,CT-A,CT-C',
      status: 'OPN_COMPLETED',
      start: '2026-08-18 08:00',
      planned: '2026-08-18',
      submitted: '2026-08-18 09:50',
      validated: '2026-08-18 10:05',
      validatedBy: 'hendra',
      closed: '2026-08-18 11:25',
      closedBy: 'ulil',
      target: 4,
      summary: {
        locations: 5,
        units: 9,
        matched: 8,
        found: 0,
        adjusted: 1,
        durations: [
          { operator: USER_IDS.budi, minutes: 110, scope: 'Pallet A1-P1, Pallet B1-P1, Container C' },
          { operator: USER_IDS.sari, minutes: 95, scope: 'Pallet A1-P2, Container A' }
        ]
      },
      log: [
        log('2026-08-18 08:00', 'Sesi dibuka — 5 tugas hitung (Budi Hartono, Sari Puspita)', 'ulil'),
        log('2026-08-18 10:05', 'Seluruh hasil hitung disahkan', 'hendra'),
        log('2026-08-18 11:25', 'Sesi ditutup — 8 matched, 1 write-off', 'ulil')
      ]
    }
  ]
  const opname_session: LocalRow[] = sessionSpecs.map(spec => {
    const id = seedId(SEED_GROUP.opnameSession, spec.n)
    const statusId = WF_OPN.statusId(spec.status)
    return {
      ...audit(spec.wh === 'RM' ? 'yulia' : 'ulil', spec.start),
      id,
      opn_no: spec.no,
      warehouse_id: spec.wh === 'RM' ? WH_RM : WH_FG,
      mode: spec.mode,
      scope: spec.scope,
      status_id: statusId,
      submission_id: submission(1600 + spec.n, 'opname_session', id, statusId, spec.start, WF_OPN.workflowId),
      started_at: utc(spec.start),
      closed_at: spec.closed ? utc(spec.closed) : null,
      planned_date: spec.planned,
      closed_by_id: spec.closedBy ? USER_IDS[spec.closedBy] : null,
      submitted_at: spec.submitted ? utc(spec.submitted) : null,
      validated_at: spec.validated ? utc(spec.validated) : null,
      validated_by_id: spec.validatedBy ? USER_IDS[spec.validatedBy] : null,
      target_hours: spec.target,
      summary: spec.summary ?? null,
      log: spec.log
    }
  })
  const sessionId = (n: number) => seedId(SEED_GROUP.opnameSession, n)

  /* ---------------- tugas ---------------- */
  type TaskSpec = {
    n: number
    no: string
    session: number
    wh: 'RM' | 'FG'
    scope: string
    kind: 'RACK' | 'PALLET' | 'CONTAINER'
    label: string
    op: UserKey
    status: 'ASSIGNED' | 'IN_PROGRESS' | 'COMPLETED'
    validation: 'PENDING' | 'VALIDATED' | null
    start?: string
    locked?: string
    counters?: Record<string, number>
    empty?: boolean
    validatedBy?: UserKey
    validatedAt?: string
  }
  const fgDone = (
    n: number,
    no: string,
    scope: string,
    kind: 'PALLET' | 'CONTAINER',
    op: UserKey,
    matched: number,
    missing = 0
  ) =>
    ({
      n,
      no,
      session: 7,
      wh: 'FG',
      scope,
      kind,
      label: `${kind === 'PALLET' ? 'Pallet' : 'Container'} ${kind === 'PALLET' ? scope : scope.replace('CT-', '')}`,
      op,
      status: 'COMPLETED',
      validation: 'VALIDATED',
      start: '2026-08-18 08:05',
      locked: '2026-08-18 09:40',
      counters: { matched, misplaced: 0, returned: 0, missing, unknown: 0, qty_diff: 0, pack_diff: 0 },
      validatedBy: 'hendra',
      validatedAt: '2026-08-18 10:05'
    }) satisfies TaskSpec
  const taskSpecs: TaskSpec[] = [
    {
      n: 1,
      no: 'OPT-260818-001',
      session: 3,
      wh: 'RM',
      scope: 'R03',
      kind: 'RACK',
      label: 'Rack R03',
      op: 'karno',
      status: 'COMPLETED',
      validation: 'PENDING',
      start: '2026-08-18 08:05',
      locked: '2026-08-18 08:35',
      counters: { matched: 4, misplaced: 0, returned: 0, missing: 1, unknown: 0, qty_diff: 0, pack_diff: 0 }
    },
    {
      n: 2,
      no: 'OPT-260818-002',
      session: 3,
      wh: 'RM',
      scope: 'R01',
      kind: 'RACK',
      label: 'Rack R01',
      op: 'tono',
      status: 'COMPLETED',
      validation: 'PENDING',
      start: '2026-08-18 08:10',
      locked: '2026-08-18 09:05',
      counters: { matched: 3, misplaced: 0, returned: 0, missing: 0, unknown: 0, qty_diff: 1, pack_diff: 0 }
    },
    {
      n: 3,
      no: 'OPT-260818-003',
      session: 3,
      wh: 'RM',
      scope: 'R02',
      kind: 'RACK',
      label: 'Rack R02',
      op: 'karno',
      status: 'COMPLETED',
      validation: 'PENDING',
      start: '2026-08-18 09:50',
      locked: '2026-08-18 10:15',
      counters: { matched: 0, misplaced: 0, returned: 0, missing: 0, unknown: 0, qty_diff: 0, pack_diff: 0 },
      empty: true
    },
    fgDone(4, 'OPT-260818-004', 'A1-P1', 'PALLET', 'budi', 2),
    fgDone(5, 'OPT-260818-005', 'A1-P2', 'PALLET', 'sari', 2, 1),
    fgDone(6, 'OPT-260818-006', 'B1-P1', 'PALLET', 'budi', 2),
    fgDone(7, 'OPT-260818-007', 'CT-A', 'CONTAINER', 'sari', 1),
    fgDone(8, 'OPT-260818-008', 'CT-C', 'CONTAINER', 'budi', 1),
    {
      n: 9,
      no: 'OPT-260818-009',
      session: 4,
      wh: 'RM',
      scope: 'R01',
      kind: 'RACK',
      label: 'Rack R01',
      op: 'karno',
      status: 'IN_PROGRESS',
      validation: null
    }
  ]
  const opname_task: LocalRow[] = taskSpecs.map(spec => ({
    ...audit(spec.wh === 'RM' ? 'yulia' : 'ulil', spec.session === 4 ? '2026-08-18 13:00' : '2026-08-18 08:00'),
    id: seedId(SEED_GROUP.opnameTask, spec.n),
    opt_no: spec.no,
    session_id: sessionId(spec.session),
    scope: spec.scope,
    scope_kind: spec.kind,
    scope_label: spec.label,
    warehouse_id: spec.wh === 'RM' ? WH_RM : WH_FG,
    operator_id: USER_IDS[spec.op],
    task_status: spec.status,
    validation_status: spec.validation,
    started_at: spec.start ? utc(spec.start) : null,
    locked_at: spec.locked ? utc(spec.locked) : null,
    completed_at: spec.locked ? utc(spec.locked) : null,
    counters: spec.counters ?? null,
    reject_reason: null,
    validated_by_id: spec.validatedBy ? USER_IDS[spec.validatedBy] : null,
    validated_at: spec.validatedAt ? utc(spec.validatedAt) : null,
    empty_confirmed: Boolean(spec.empty)
  }))
  const taskId = (n: number) => seedId(SEED_GROUP.opnameTask, n)

  /* ---------------- rincian pindai (sesi RM 260818-001) ---------------- */
  type ScanSpec = [
    n: number,
    task: number,
    qr: string,
    good: number,
    ng: number,
    reason: string | null,
    cls: string,
    at: string
  ]
  const scanSpecs: ScanSpec[] = [
    [1, 1, 'PPR-REG:20260801:01:0004', 20, 0, null, 'MATCHED', '08:12'],
    [2, 1, 'FLT-B06:20260801:01:0003', 13, 2, 'SOBEK', 'MATCHED', '08:18'],
    [3, 1, 'ETK-BOLD20:20260801:01:0002', 14, 0, null, 'MATCHED', '08:24'],
    [4, 1, 'ETK-XTRA20:20260805:01:0001', 2, 0, null, 'MATCHED', '08:29'],
    [5, 1, 'ETK-XTRA20:20260801:01:0005', 0, 0, null, 'MISSING', '08:35'],
    [6, 2, 'TSG-MLD:20260801:01:0001', 17, 1, 'LEMBAB', 'MATCHED', '08:22'],
    [7, 2, 'TSG-REG:20260814:01:0001', 7, 0, null, 'MATCHED', '08:31'],
    [8, 2, 'TSG-REG:20260816:01:0001', 88, 0, null, 'QTY_LESS', '08:44'],
    [9, 2, 'TSG-MLD:20260818:01:0001', 40, 0, null, 'MATCHED', '08:58']
  ]
  const opname_scan: LocalRow[] = scanSpecs.map(([n, task, qr, good, ng, reason, cls, at]) => {
    const unit = unitOf(qr)
    const system = Math.round(Number(unit.qty_small ?? 0) / perBig(unit))
    const bin = String((seed.mt_rack_bin ?? []).find(row => row.id === unit.rack_bin_id)?.code ?? '')
    return {
      ...audit(task === 1 ? 'karno' : 'tono', `2026-08-18 ${at}`),
      id: seedId(SEED_GROUP.opnameScan, n),
      task_id: taskId(task),
      session_id: sessionId(3),
      rack_bin_id: unit.rack_bin_id ?? null,
      warehouse_zone_id: unit.warehouse_zone_id ?? null,
      scanned_code: qr,
      material_id: unit.material_id,
      sku_id: null,
      unit_id: unit.id,
      label_id: null,
      pack_stock_id: null,
      qty_system: system,
      qty_good: good,
      qty_ng: ng,
      ng_reason_id: reason ? rmFindingReasonId(reason) : null,
      ng_reason_ids: reason ? [rmFindingReasonId(reason)] : [],
      unit_name: String(unit.unit_big_snapshot ?? ''),
      scan_class: cls,
      scanned_at: utc(`2026-08-18 ${at}`),
      from_location: bin,
      decision: null,
      to_location: null
    }
  })

  /* ---------------- selisih ---------------- */
  type DiscSpec = {
    n: number
    no: string
    status: DsrStatusCode
    wh: 'RM' | 'FG'
    session: number | null
    task: number | null
    source: 'OPNAME' | 'PICKING'
    type: string
    unit?: LocalRow
    label?: LocalRow
    qty: number
    unitName: string
    location: string
    at: string
    by: UserKey
    trace: string
    escalated?: string
    approved?: string
    approver?: UserKey
    note?: string
    qtySystem?: number
    qtyPhysical?: number
    log: Log[]
  }
  const xtra = unitOf('ETK-XTRA20:20260801:01:0005')
  const discSpecs: DiscSpec[] = [
    {
      n: 1,
      no: 'DSC-260818-001',
      status: 'DSR_INVESTIGATION',
      wh: 'RM',
      session: 3,
      task: 1,
      source: 'OPNAME',
      type: 'MISSING',
      unit: xtra,
      qty: Math.round(Number(xtra.qty_small) / perBig(xtra)),
      unitName: 'Box',
      location: 'R03-B02-L02-P01',
      at: '2026-08-18 08:35',
      by: 'karno',
      trace: 'Terakhir putaway: Karno · 2026-08-01 09:10',
      log: [log('2026-08-18 08:35', 'Tidak ditemukan saat hitung Rack R03 (OPT-260818-001)', 'karno')]
    },
    {
      n: 2,
      no: 'DSC-260818-002',
      status: 'DSR_PENDING_APP',
      wh: 'RM',
      session: null,
      task: null,
      source: 'PICKING',
      type: 'MISSING',
      unit: extraUnits[0],
      qty: 10,
      unitName: 'Box',
      location: 'R01-B01-L01-P01',
      at: '2026-08-10 10:15',
      by: 'tono',
      trace: 'Sudah dicek ulang, tetap tidak ditemukan di rak maupun rak sebelah',
      escalated: '2026-08-16 01:00',
      log: [
        log('2026-08-10 10:15', 'Tidak ditemukan saat picking PICK-260810-004 — selisih dicatat untuk opname', 'tono'),
        log('2026-08-16 01:00', 'Window investigasi H+5 lewat — dieskalasi otomatis ke Kadiv WH RM', null)
      ]
    },
    {
      n: 3,
      no: 'DSC-260818-003',
      status: 'DSR_INVESTIGATION',
      wh: 'RM',
      session: 3,
      task: 2,
      source: 'OPNAME',
      type: 'QTY_LESS',
      unit: reg,
      qty: 2,
      unitName: 'Sak',
      location: 'R01-B03-L02-P01',
      at: '2026-08-18 09:05',
      by: 'tono',
      trace: 'Dihitung 88 Sak, sistem mencatat 90 Sak — Terakhir putaway: Karno · 2026-08-16',
      qtySystem: 90,
      qtyPhysical: 88,
      log: [
        log('2026-08-18 09:05', 'Selisih jumlah tumpukan saat hitung Rack R01 (OPT-260818-002): 88 dari 90 Sak', 'tono')
      ]
    },
    {
      n: 4,
      no: 'DSC-260715-001',
      status: 'DSR_ADJUSTED_WRITE_OFF',
      wh: 'RM',
      session: 2,
      task: null,
      source: 'OPNAME',
      type: 'MISSING',
      unit: extraUnits[1],
      qty: 4,
      unitName: 'Sak',
      location: 'R02-B01-L02-P01',
      at: '2026-07-15 11:20',
      by: 'karno',
      trace: 'Sisa retur WIP, terakhir disimpan 2026-07-02 oleh Karno',
      escalated: '2026-07-21 01:00',
      approved: '2026-07-21 15:30',
      approver: 'alex',
      note: 'Sudah dua kali cek ulang, tidak ditemukan — disetujui dihapus',
      log: [
        log('2026-07-15 11:20', 'Tidak ditemukan saat hitung Rack R02', 'karno'),
        log('2026-07-21 01:00', 'Window investigasi H+5 lewat — dieskalasi otomatis ke Kadiv WH RM', null),
        log('2026-07-21 15:30', 'Write-off disetujui — Adjusted - Not Found', 'alex')
      ]
    },
    {
      n: 5,
      no: 'DSC-260818-005',
      status: 'DSR_ADJUSTED_WRITE_OFF',
      wh: 'FG',
      session: 7,
      task: 5,
      source: 'OPNAME',
      type: 'MISSING',
      label: extraLabels[1],
      qty: 1,
      unitName: 'Karton',
      location: 'A1-P2',
      at: '2026-08-18 09:40',
      by: 'sari',
      trace: 'Terakhir putaway ke pallet A1-P2 — label tidak ditemukan saat hitung fisik',
      escalated: '2026-08-18 10:15',
      approved: '2026-08-18 10:40',
      approver: 'ratna',
      note: 'Sudah dicek ke pallet sebelah dan area kumpul, tidak ditemukan',
      log: [
        log('2026-08-18 09:40', 'Tidak ditemukan saat hitung Pallet A1-P2 (OPT-260818-005)', 'sari'),
        log('2026-08-18 10:15', 'Dieskalasi ke Kadiv WH FG', 'ulil'),
        log('2026-08-18 10:40', 'Write-off disetujui — Adjusted - Not Found', 'ratna')
      ]
    },
    {
      n: 6,
      no: 'DSC-260817-004',
      status: 'DSR_PENDING_APP',
      wh: 'FG',
      session: null,
      task: null,
      source: 'PICKING',
      type: 'MISSING',
      label: extraLabels[0],
      qty: 1,
      unitName: 'Slop',
      location: 'CT-A',
      at: '2026-08-17 14:05',
      by: 'budi',
      trace: 'Temuan dini saat picking — label tidak ada di container',
      escalated: '2026-08-17 16:00',
      log: [
        log('2026-08-17 14:05', 'Tidak ditemukan saat picking FG — selisih dicatat untuk opname', 'budi'),
        log('2026-08-17 16:00', 'Dieskalasi ke Kadiv WH FG', 'ulil')
      ]
    }
  ]
  const discrepancy: LocalRow[] = discSpecs
    .filter(spec => spec.unit || spec.label)
    .map(spec => {
      const id = seedId(SEED_GROUP.discrepancy, spec.n)
      const statusId = WF_DSR.statusId(spec.status)
      const created = utc(spec.at)
      const days = spec.wh === 'RM' ? [5, 7] : [0, 1]
      return {
        ...audit(spec.by, spec.at),
        id,
        dsc_no: spec.no,
        unit_id: spec.unit?.id ?? null,
        label_id: spec.label?.id ?? null,
        transfer_id: null,
        source_type: spec.source,
        qty: spec.qty,
        rack_bin_id: spec.wh === 'RM' ? binId(spec.location) : null,
        warehouse_zone_id: null,
        disc_type: spec.type,
        status_id: statusId,
        submission_id: submission(1700 + spec.n, 'discrepancy', id, statusId, spec.at, WF_DSR.workflowId),
        window_due_at: dueEndOfDay(created, days[0]),
        escalated_at: spec.escalated ? utc(spec.escalated) : null,
        approver_id: spec.approver ? USER_IDS[spec.approver] : null,
        note: spec.note ?? null,
        session_id: spec.session ? sessionId(spec.session) : null,
        warehouse_id: spec.wh === 'RM' ? WH_RM : WH_FG,
        task_id: spec.task ? taskId(spec.task) : null,
        material_id: spec.unit?.material_id ?? null,
        sku_id: spec.label?.sku_id ?? null,
        location_code: spec.location,
        scanned_code: String(spec.unit?.qr_code ?? spec.label?.label_no ?? ''),
        unit_name: spec.unitName,
        qty_system: spec.qtySystem ?? null,
        qty_physical: spec.qtyPhysical ?? null,
        trace: spec.trace,
        approval_due_at: dueEndOfDay(created, days[1]),
        recheck_operator_id: null,
        recheck_note: null,
        resolved_at: spec.approved ? utc(spec.approved) : null,
        resolved_by_id: spec.approver ? USER_IDS[spec.approver] : null,
        approved_at: spec.approved ? utc(spec.approved) : null,
        reject_reason: null,
        parent_id: null,
        found_qty: null,
        log: spec.log
      }
    })

  return {
    ...seed,
    rm_unit: [...(seed.rm_unit ?? []), ...extraUnits],
    fg_label: [...fgLabels, ...extraLabels],
    opname_schedule,
    opname_session,
    opname_task,
    opname_scan,
    discrepancy,
    wxl_submission: [...(seed.wxl_submission ?? []), ...wxl_submission]
  }
}
